import React, { useCallback, useContext, useMemo, useState } from 'react';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import { DateTime } from 'luxon';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

import AccountPickerContext from 'contexts/AccountPickerContext';
import CurrentUserContext from 'contexts/CurrentUserContext';
import DashboardsContext from 'contexts/DashboardsContext';
import ReportContext from 'contexts/ReportContext';
import { buildDashboardShow } from 'navigation/appRoutes';
import { PortalsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import Button from 'kingpin/atoms/Button';
import { Option } from 'kingpin/atoms/ContextMenu';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import Form from 'kingpin/forms/Form';
import FormContent from 'kingpin/forms/FormContent';
import FormHeader from 'kingpin/forms/FormHeader';
import Row from 'components/Common/Row';
import Inputs from 'components/Inputs';
import Search from 'components/Search';
import AccessInput from 'components/AccessInput';
import useLiveGadgetCopySettings from 'hooks/useLiveGadgetCopySettings';
import usePopup from 'hooks/usePopup';
import STORE from 'store';
import buildCardLayout from 'contextProviders/WidgetGalleryProvider/buildCardLayout';
import findStartingY from 'contextProviders/WidgetGalleryProvider/findStartingY';
import withoutNulls from 'api/expression/withoutNulls';
import getIdentifier from 'getIdentifier';
import createDashboardGadget from 'api/createDashboardGadget';
import { buildDashboardGadgetFromSettings } from 'utils/copyGadgetSettings';

const List = styled.div`
  margin-bottom: 24px;
  height: 400px;
  overflow-y: auto;
  padding-right: 12px;
`;

const Item = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 8px;
  cursor: pointer;
  user-select: none;
  height: 32px;
  &:hover {
    background-color: var(--color-bg-hover);
  }
`;

const CopyReportGadgetToDashboardButton = ({
  reportGadget,
}: {
  reportGadget: ReportGadgets.ReportGadget;
}) => {
  const { allDashboards } = useContext(DashboardsContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { selectedPortal } = useContext(PortalsContext);
  const { report } = useContext(ReportContext);
  const navigate = useNavigate();
  const { isOpen, open, close } = usePopup(false, undefined, true);
  const { settings, w, h } = useLiveGadgetCopySettings();

  const [step, setStep] = useState<1 | 2>(1);
  const [copyTo, setCopyTo] = useState<
    'new dashboard' | 'existing dashboard' | undefined
  >(undefined);
  const [searchText, setSearchText] = useState('');
  const [selectedDashboard, setSelectedDashboard] = useState<
    DashboardType | undefined
  >(undefined);
  const [title, setTitle] = useState('');
  const [access, setAccess] = useState<ResourceAccess>({
    version: '2',
    type: 'Private',
  });
  const [isLoading, setIsLoading] = useState(false);

  const availableDashboards = useMemo(
    () =>
      allDashboards
        .filter((d) => !d.isWallboardSlide)
        .filter((d) => d.templateId === undefined),
    [allDashboards],
  );

  const filteredDashboards = useMemo(() => {
    if (searchText === '') {
      return availableDashboards;
    }
    const lower = searchText.toLowerCase();
    return availableDashboards.filter((d) =>
      d.name.toLowerCase().includes(lower),
    );
  }, [availableDashboards, searchText]);

  const radioOptions: RadioOption[] = useMemo(
    () => [
      {
        key: 'new dashboard',
        label: 'New Dashboard',
        isSelected: copyTo === 'new dashboard',
        onSelected: () => setCopyTo('new dashboard'),
        testId: 'copy-to-new-dashboard',
      },
      {
        key: 'existing dashboard',
        label: 'Existing Dashboard',
        isSelected: copyTo === 'existing dashboard',
        onSelected: () => setCopyTo('existing dashboard'),
        testId: 'copy-to-existing-dashboard',
      },
    ],
    [copyTo],
  );

  const resetState = useCallback(() => {
    setStep(1);
    setCopyTo(undefined);
    setSearchText('');
    setSelectedDashboard(undefined);
    setTitle('');
    setAccess({
      version: '2',
      type: 'Private',
    });
    setIsLoading(false);
  }, []);

  const onClose = useCallback(() => {
    close();
    resetState();
  }, [close, resetState]);

  const saveGadgetToDashboard = useCallback(
    async (targetDashboard: DashboardType) => {
      setIsLoading(true);
      const now = DateTime.utc().toISO();
      const newGadgetId = getIdentifier();

      const newDashboardGadget = buildDashboardGadgetFromSettings({
        id: newGadgetId,
        chartDef: reportGadget.chartDef,
        settings,
        currentUserId,
        now,
        reportDrillDownId: report?.id,
      });

      await createDashboardGadget(
        withoutNulls(newDashboardGadget),
        selectedAccountId,
      );

      const cards = targetDashboard.canvas.cards;
      const card: CanvasCard.DashboardGadgetCard = {
        layout: buildCardLayout(
          0,
          findStartingY(cards, 'desktop'),
          newGadgetId,
          w,
          h,
        ),
        content: {
          type: 'Dashboard Gadget',
          dashboardGadgetId: newDashboardGadget.id,
        },
      };

      const newCanvas: Canvas = {
        ...targetDashboard.canvas,
        cards: [...cards, card],
      };

      const updatedDashboard: DashboardType = {
        ...targetDashboard,
        canvas: newCanvas,
        updatedOn: now,
        updatedBy: currentUserId,
      };

      await STORE.getDashboardsRef({ accountId: selectedAccountId })
        .doc(targetDashboard.id)
        .set(withoutNulls(updatedDashboard));

      setIsLoading(false);
      onClose();
      navigate(
        buildDashboardShow({
          id: targetDashboard.id,
          isEditing: true,
          portal: selectedPortal,
        }),
      );
    },
    [
      currentUserId,
      h,
      navigate,
      onClose,
      report?.id,
      reportGadget.chartDef,
      selectedAccountId,
      selectedPortal,
      settings,
      w,
    ],
  );

  const createNewDashboard = useCallback(async () => {
    setIsLoading(true);
    const now = DateTime.utc().toISO();
    const newGadgetId = getIdentifier();
    const newDashboardId = getIdentifier();

    const newDashboardGadget = buildDashboardGadgetFromSettings({
      id: newGadgetId,
      chartDef: reportGadget.chartDef,
      settings,
      currentUserId,
      now,
      reportDrillDownId: report?.id,
    });

    await createDashboardGadget(
      withoutNulls(newDashboardGadget),
      selectedAccountId,
    );

    const card: CanvasCard.DashboardGadgetCard = {
      layout: buildCardLayout(0, 0, newGadgetId, w, h),
      content: {
        type: 'Dashboard Gadget',
        dashboardGadgetId: newDashboardGadget.id,
      },
    };

    const newDashboard: DashboardType = {
      version: '5',
      id: newDashboardId,
      name: title,
      canvas: {
        cards: [card],
      },
      isTemplate: false,
      variableDrillDowns: [],
      access,
      isV3Published: false,
      v3Access: {
        isPublic: false,
        groupIds: [],
        userIds: [],
      },
      labelIds: [],
      createdBy: currentUserId,
      createdOn: now,
      updatedBy: currentUserId,
      updatedOn: now,
    };

    await STORE.getDashboardsRef({ accountId: selectedAccountId })
      .doc(newDashboard.id)
      .set(withoutNulls(newDashboard));

    setIsLoading(false);
    onClose();
    navigate(
      buildDashboardShow({
        id: newDashboard.id,
        isEditing: true,
        portal: selectedPortal,
      }),
    );
  }, [
    access,
    currentUserId,
    h,
    navigate,
    onClose,
    report?.id,
    reportGadget.chartDef,
    selectedAccountId,
    selectedPortal,
    settings,
    title,
    w,
  ]);

  const onCopyClicked = useCallback(() => {
    if (copyTo === 'new dashboard') {
      setStep(2);
    } else if (copyTo === 'existing dashboard') {
      setStep(2);
    }
  }, [copyTo]);

  const onSaveClicked = useCallback(async () => {
    if (copyTo === 'new dashboard') {
      await createNewDashboard();
    } else if (copyTo === 'existing dashboard' && selectedDashboard) {
      await saveGadgetToDashboard(selectedDashboard);
    }
  }, [copyTo, createNewDashboard, saveGadgetToDashboard, selectedDashboard]);

  const onBackClicked = useCallback(() => {
    setStep(1);
    setSearchText('');
    setSelectedDashboard(undefined);
    setTitle('');
  }, []);

  const onSearchTextChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(event.target.value);
    },
    [],
  );

  const onTitleChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setTitle(event.target.value);
    },
    [],
  );

  return (
    <React.Fragment>
      <Option
        label={'Copy to Dashboard'}
        icon={'copy'}
        onClick={open}
        shouldHideInsteadOfCloseMenu
      />
      <ModalTransition>
        {isOpen && (
          <Modal
            onClose={onClose}
            shouldScrollInViewport={false}
            autoFocus={false}
          >
            {step === 1 && (
              <Form data-testid="copy-report-gadget-to-dashboard-flow">
                <FormHeader title={'Copy gadget to'} onClose={onClose} />
                <FormContent>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 4 }}>
                      <Typography.Body type={'Label'}>
                        Destination
                      </Typography.Body>
                    </div>
                    <Inputs.Radio options={radioOptions} />
                  </div>
                </FormContent>
                <Row
                  style={{
                    justifyContent: 'flex-end',
                    marginBottom: 18,
                  }}
                  centerAlign
                >
                  <Row centerAlign>
                    <div style={{ marginRight: 12 }}>
                      <Button
                        onClick={onClose}
                        size="Small"
                        type="Ghost"
                        label="Cancel"
                      />
                    </div>
                    <Button
                      type="Primary"
                      size="Small"
                      label="Copy"
                      onClick={onCopyClicked}
                      isDisabled={copyTo === undefined}
                    />
                  </Row>
                </Row>
              </Form>
            )}
            {step === 2 && copyTo === 'new dashboard' && (
              <Form>
                <FormHeader title={'Create Dashboard'} onClose={onClose} />
                <FormContent>
                  <div style={{ marginBottom: 16 }}>
                    <Typography.Body type="Label">
                      Dashboard Title (max. 40 characters)
                    </Typography.Body>
                    <Inputs.TextInput
                      value={title}
                      onChange={onTitleChanged}
                      data-testid={'dashboard-title'}
                    />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <AccessInput
                      access={access}
                      setAccess={setAccess}
                      canSetToPrivate={true}
                    />
                  </div>
                </FormContent>
                <Row centerAlign spaceBetween style={{ marginBottom: 16 }}>
                  <Button
                    onClick={onClose}
                    isDisabled={isLoading}
                    type="Ghost"
                    size="Small"
                    label="Cancel"
                  />
                  <Row centerAlign>
                    <div style={{ marginRight: 12 }}>
                      <Button
                        type="Ghost"
                        size="Small"
                        onClick={onBackClicked}
                        isDisabled={isLoading}
                        label="Back"
                      />
                    </div>
                    <Button
                      type="Primary"
                      size="Small"
                      label="Save"
                      onClick={onSaveClicked}
                      isLoading={isLoading}
                      isDisabled={title === '' || isLoading}
                    />
                  </Row>
                </Row>
              </Form>
            )}
            {step === 2 && copyTo === 'existing dashboard' && (
              <Form data-testid="copy-report-gadget-to-dashboard-flow">
                <FormHeader title={'Select Dashboard'} onClose={onClose} />
                <FormContent>
                  <div style={{ marginBottom: 16 }}>
                    <Search
                      noBorder
                      onSearchTextChanged={onSearchTextChanged}
                      searchText={searchText}
                      onClearClicked={() => {
                        setSearchText('');
                      }}
                    />
                  </div>
                  <List>
                    {filteredDashboards.map((d) => (
                      <Item
                        key={d.id}
                        onClick={() => {
                          if (
                            selectedDashboard &&
                            selectedDashboard.id === d.id
                          ) {
                            setSelectedDashboard(undefined);
                          } else {
                            setSelectedDashboard(d);
                          }
                        }}
                      >
                        <Row
                          centerAlign
                          style={{
                            justifyContent: 'space-between',
                            width: '100%',
                          }}
                        >
                          <Typography.Body type="Body 12">
                            {d.name}
                          </Typography.Body>
                          {!!selectedDashboard &&
                            selectedDashboard.id === d.id && (
                              <Icon icon="checkmark" />
                            )}
                        </Row>
                      </Item>
                    ))}
                  </List>
                </FormContent>
                <Row centerAlign spaceBetween style={{ marginBottom: 16 }}>
                  <Button
                    onClick={onClose}
                    isDisabled={isLoading}
                    label="Cancel"
                    size="Small"
                    type="Ghost"
                  />
                  <Row centerAlign>
                    <div style={{ marginRight: 12 }}>
                      <Button
                        onClick={onBackClicked}
                        isDisabled={isLoading}
                        label="Back"
                        size="Small"
                        type="Ghost"
                      />
                    </div>
                    <Button
                      type="Primary"
                      size="Small"
                      onClick={onSaveClicked}
                      isDisabled={isLoading || !selectedDashboard}
                      isLoading={isLoading}
                      label="Save"
                    />
                  </Row>
                </Row>
              </Form>
            )}
          </Modal>
        )}
      </ModalTransition>
    </React.Fragment>
  );
};

export default CopyReportGadgetToDashboardButton;
